import React, { useState, useEffect } from 'react';
import { X, Share, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DISMISSED_KEY = 'pwa-install-dismissed';

/**
 * PWAInstallBanner
 *
 * Centered modal dialog prompting the user to install the PWA.
 * - Android/Chrome: uses the captured beforeinstallprompt event
 * - iOS Safari:     shows manual "Share → Add to Home Screen" instructions
 *
 * Props:
 *   show       — auto-show based on current route (from Layout)
 *   forceShow  — bypass dismissed state (e.g. triggered from menu)
 *   onClose    — called when the dialog closes (for forceShow cleanup)
 */
export function PWAInstallBanner({ show = false, forceShow = false, onClose }) {
  const { installPrompt, clearInstallPrompt } = useAuth();
  const [dismissed, setDismissed]     = useState(false);
  const [isIOS, setIsIOS]             = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [phase, setPhase]             = useState('idle'); // 'idle' | 'installing' | 'success'

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768;

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) {
      setDismissed(true);
    }
    const ua = window.navigator.userAgent;
    setIsIOS(/iphone|ipad|ipod/i.test(ua));
    if (
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    ) {
      setIsInstalled(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    clearInstallPrompt();
    if (onClose) onClose();
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    setPhase('installing');
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      clearInstallPrompt();
      setPhase('success');
    } else {
      setPhase('idle');
      handleDismiss();
    }
  };

  // Guard clauses
  if (!isMobile) return null;
  if (isInstalled) return null;
  if (!forceShow && (dismissed || !show)) return null;
  // Auto-show requires a live install prompt or iOS. Force-show (from menu)
  // always renders — the no-prompt case is handled gracefully in the UI below.
  if (!forceShow && !isIOS && !installPrompt && phase !== 'success') return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={phase === 'idle' ? handleDismiss : undefined}
        aria-hidden="true"
      />

      {/* Centered modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center px-6 pointer-events-none">
        <div
          role="dialog"
          aria-label="Install WANTD app"
          aria-modal="true"
          className="w-full max-w-xs bg-[var(--theme-card)] border border-[var(--theme-border)]
                     rounded-2xl shadow-xl p-6 relative pointer-events-auto"
        >

          {phase === 'success' ? (
            /* ── Success state ── */
            <div className="flex flex-col items-center text-center gap-4 py-2">
              <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-[#059669]" />
              </div>
              <div>
                <p className="text-base font-bold text-[var(--theme-text-primary)]">
                  WANTD installed!
                </p>
                <p className="text-sm text-[var(--theme-text-secondary)] mt-1 leading-relaxed">
                  You can close this tab and launch WANTD directly from your home screen.
                </p>
              </div>
              <button
                onClick={() => { setDismissed(true); if (onClose) onClose(); }}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold
                           bg-[#1E3A5F] text-white hover:bg-[#162d4a] transition-colors"
              >
                OK
              </button>
            </div>
          ) : (
            /* ── Idle / installing state ── */
            <>
              {/* Dismiss (X) button */}
              {phase === 'idle' && (
                <button
                  onClick={handleDismiss}
                  className="absolute top-3 right-3 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Logo + heading */}
              <div className="flex flex-col items-center text-center gap-3 mb-5">
                <img
                  src="/icons/icon-192.png"
                  alt="WANTD"
                  className="w-16 h-16 rounded-2xl"
                />
                <div>
                  <p className="text-lg font-bold text-[var(--theme-text-primary)]">
                    Install the WANTD app?
                  </p>
                  <p className="text-sm text-[var(--theme-text-secondary)] mt-1 leading-relaxed">
                    Get instant alerts, offline browsing and a full-screen experience — no App Store required.
                  </p>
                </div>
              </div>

              {isIOS ? (
                /* iOS — manual instructions */
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-xs text-[var(--theme-text-secondary)]
                                  bg-[var(--theme-hover)] rounded-lg px-3 py-2.5">
                    <Share className="w-4 h-4 shrink-0 text-[#1E3A5F]" />
                    <span>
                      Tap <strong className="text-[var(--theme-text-primary)]">Share</strong>{' '}
                      then{' '}
                      <strong className="text-[var(--theme-text-primary)]">
                        Add to Home Screen
                      </strong>
                    </span>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium
                               border border-[#1E3A5F] text-[#1E3A5F] bg-white
                               hover:bg-[#1E3A5F]/5 transition-colors"
                  >
                    No thanks
                  </button>
                </div>
              ) : !installPrompt ? (
                /* No install prompt yet — show browser-menu fallback */
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-xs text-[var(--theme-text-secondary)]
                                  bg-[var(--theme-hover)] rounded-lg px-3 py-2.5">
                    <Share className="w-4 h-4 shrink-0 text-[#1E3A5F]" />
                    <span>
                      Tap your browser's <strong className="text-[var(--theme-text-primary)]">menu (⋮)</strong>{' '}
                      and select{' '}
                      <strong className="text-[var(--theme-text-primary)]">Add to Home Screen</strong>
                    </span>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium
                               border border-[#1E3A5F] text-[#1E3A5F] bg-white
                               hover:bg-[#1E3A5F]/5 transition-colors"
                  >
                    Got it
                  </button>
                </div>
              ) : (
                /* Android / Chrome — native prompt */
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleInstall}
                    disabled={phase === 'installing'}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                               bg-[#1E3A5F] text-white text-sm font-semibold
                               hover:bg-[#162d4a] disabled:opacity-70 transition-colors"
                  >
                    {phase === 'installing' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {phase === 'installing' ? 'Installing…' : 'Install'}
                  </button>
                  <button
                    onClick={handleDismiss}
                    disabled={phase === 'installing'}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium
                               border border-[#1E3A5F] text-[#1E3A5F] bg-white
                               hover:bg-[#1E3A5F]/5 disabled:opacity-50 transition-colors"
                  >
                    No thanks
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
